import { Form, FormAssertive, FormSubmit, formatDateToFormInput, useForm } from "@/components/form"
import { initialFiles } from "@/components/form/files"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { UseDialogFormProps, UseDialogProps } from "@/components/ui/hooks/useDialog"
import { formatError } from "@/fns/format-error"
import { useMemoOnce } from "@/hooks/useMemoOnce"
import { resetAllStoresAndReload } from "@/store"
import { updateInvoice } from "@/store/invoices/actions"
import { Invoice } from "@/store/invoices/localizers"
import { match } from "ts-pattern"
import {
  FormValues,
  InvoiceForm,
  basicFields,
  formValuesToPayload,
  invoiceValidator,
} from "./InvoiceForm"

/**
 * EditDialog
 */
export const EditDialog: React.FC<UseDialogProps<Invoice>> = ({ item, onOpenChange, open }) => {
  return (
    <Dialog {...{ open, onOpenChange }} title="Edit invoice" className="max-w-2xl">
      {item !== false && <DialogForm {...{ item, onOpenChange }} />}
    </Dialog>
  )
}

const DialogForm: React.FC<UseDialogFormProps<Invoice>> = ({ item, onOpenChange }) => {
  const form = useForm<FormValues>({
    allowSubmitAttempt: true,
    allowErrorSubmit: true,
    values: useMemoOnce(() => ({
      ...D.selectKeys(item, basicFields),
      date: formatDateToFormInput(item.date),
      dueDate: formatDateToFormInput(item.dueDate),
      attachment: G.isNull(item.attachment) ? [] : initialFiles([item.attachment]),
      lines: [],
      linesById: {},
    })),
    validate: invoiceValidator,
    onSubmit: async ({ values }) => {
      if (!form.isValid && !form.attemptedSubmit) return formatError("VALIDATION_FAILURE")
      return match(await updateInvoice(item.id, formValuesToPayload(values)))
        .with({ error: false }, () => {
          toast.success("Invoice updated")
          onOpenChange(false)
        })
        .otherwise(({ code }) =>
          match(code)
            .with("VALIDATION_FAILURE", formatError)
            .with("INVALID_AUTH_SESSION", resetAllStoresAndReload)
            .with("FETCH_ERROR", code => toast.error(formatError(code)))
        )
    },
  })

  return (
    <Form form={form} className="grid gap-6">
      <FormAssertive />
      <InvoiceForm />
      <Dialog.Footer>
        <Dialog.Close asChild>
          <Button variant="secondary">Back</Button>
        </Dialog.Close>
        <FormSubmit>
          {!form.isSubmitting && form.attemptedSubmit ? "Update despite errors" : "Update"}
        </FormSubmit>
      </Dialog.Footer>
    </Form>
  )
}
